import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ThemeProvider } from 'styled-components';
import GlobalStyles from './styles/GlobalStyles';
import theme from './styles/theme';
import { initSmoothScrolling } from './utils/smoothScroll';

// Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/layout/ScrollToTop';
import ScrollToTopOnRouteChange from './components/layout/ScrollToTopOnRouteChange';
import ScrollProgress from './components/layout/ScrollProgress';
import Preloader from './components/layout/Preloader';

// Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import HowRPLWorksPage from './pages/HowRPLWorksPage';
import FAQPage from './pages/FAQPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import BlogPage from './pages/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage';
import CoursesPage from './pages/CoursesPage';
import FreeQuotePage from './pages/FreeQuotePage';
import CourseDetailPage from './pages/CourseDetailPage';
import CategoryPage from './pages/CategoryPage';
import SearchResultsPage from './pages/SearchResultsPage';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminCategoriesPage from './pages/admin/AdminCategoriesPage';
import AdminCoursesPage from './pages/admin/AdminCoursesPage';
import AdminBlogsPage from './pages/admin/AdminBlogsPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';

// Context
import { AuthProvider } from './context/AuthContext';

// Components
import ProtectedRoute from './components/admin/ProtectedRoute';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Component to handle preloader logic inside Router context
const AppContent = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Initialize smooth scrolling
    initSmoothScrolling();
  }, []);

  // Handle preloader on route changes
  useEffect(() => {
    // Immediate scroll to top - happens before any rendering
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    setIsLoading(true);
    setIsExiting(false);
    
    // Add body class to prevent scrolling during preloader
    document.body.classList.add('preloader-active');

    // Shorter preloader time to reduce visual jump
    const timer = setTimeout(() => {
      setIsExiting(true);
      // Wait for exit animation to complete before hiding
      setTimeout(() => {
        setIsLoading(false);
        // Remove body class to restore scrolling
        document.body.classList.remove('preloader-active');
        
        // Final scroll to top to ensure position
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 300); // Shorter exit animation
    }, 500); // Shorter preloader display time

    return () => {
      clearTimeout(timer);
      // Cleanup: remove body class if component unmounts
      document.body.classList.remove('preloader-active');
    };
  }, [location.pathname]); // Trigger on route change

  return (
    <>
      <Preloader isVisible={isLoading} isExiting={isExiting} />
      <ScrollToTopOnRouteChange />
      <div className="App">
        <ScrollProgress />
        <Header />
        <main>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/how-rpl-works" element={<HowRPLWorksPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-conditions" element={<TermsConditionsPage />} />
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/courses/:courseCode" element={<CourseDetailPage />} />
            <Route path="/category/:slug" element={<CategoryPage />} />
            <Route path="/search" element={<SearchResultsPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogDetailPage />} />
            <Route path="/free-quote" element={<FreeQuotePage />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin/dashboard" element={
              <ProtectedRoute>
                <AdminDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/categories" element={
              <ProtectedRoute>
                <AdminCategoriesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/courses" element={
              <ProtectedRoute>
                <AdminCoursesPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/blogs" element={
              <ProtectedRoute>
                <AdminBlogsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/settings" element={
              <ProtectedRoute>
                <AdminSettingsPage />
              </ProtectedRoute>
            } />
          </Routes>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
};

function App() {
  // Disable browser scroll restoration globally
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;